import { http } from './http'
import {
  AvailableTokenPool,
  BadgeDto,
  BadgeUpsertDto,
  ContinuousQuestSummary,
  LeaderboardDto,
  LeaderboardStandingsDto,
  LeaderboardUpsertDto,
  PartnerDto,
  PartnerUpsertDto,
  QuestDto,
  QuestGroupDto,
  QuestGroupUpsertDto,
  QuestUpsertDto,
  RankUpsert,
  RewardDefinitionDto,
  RewardDefinitionUpsertDto,
  RewardPoolDto,
  RewardPoolUpsertDto,
  RewardRuleDto,
  RewardRuleUpsertDto,
  RoadmapDto,
  RoadmapRank,
  RoadmapUpsertDto,
  StepDependencyDto,
  StepDependencyResponseDto,
  StepDto,
  StepUpsertDto,
  TagDto,
  TagUpsertDto,
} from '../../common/models/gamification'

/**
 * Thin wrappers over the operations gamification admin CRUD.
 * Auth (`Authorization` + `x-festival-name`) is injected by the axios interceptor;
 * the gateway injects `x-user-id`. Edition-scoped roots (partners, roadmaps) take
 * `festivalEditionId` as a query param on list + create only (immutable on update).
 */
const BASE = '/operations/gamification'

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export const getPartners = (festivalEditionId: string) =>
  http.get<PartnerDto[]>(`${BASE}/partners`, { params: { festivalEditionId } })

export const getPartner = (id: string) =>
  http.get<PartnerDto>(`${BASE}/partners/${id}`)

export const createPartner = (
  festivalEditionId: string,
  data: PartnerUpsertDto,
) =>
  http.post<PartnerDto>(`${BASE}/partners`, data, {
    params: { festivalEditionId },
  })

export const updatePartner = (id: string, data: PartnerUpsertDto) =>
  http.put<PartnerDto>(`${BASE}/partners/${id}`, data)

export const deletePartner = (id: string) =>
  http.delete<void>(`${BASE}/partners/${id}`)

// ---------------------------------------------------------------------------
// Roadmaps
// ---------------------------------------------------------------------------

/** Shallow list projection (steps = null). */
export const getRoadmaps = (festivalEditionId: string) =>
  http.get<RoadmapDto[]>(`${BASE}/roadmaps`, { params: { festivalEditionId } })

/** Full roadmap incl. nested steps + resolved targets. */
export const getRoadmap = (id: string) =>
  http.get<RoadmapDto>(`${BASE}/roadmaps/${id}`)

export const createRoadmap = (
  festivalEditionId: string,
  data: RoadmapUpsertDto,
) =>
  http.post<RoadmapDto>(`${BASE}/roadmaps`, data, {
    params: { festivalEditionId },
  })

export const updateRoadmap = (id: string, data: RoadmapUpsertDto) =>
  http.put<RoadmapDto>(`${BASE}/roadmaps/${id}`, data)

export const deleteRoadmap = (id: string) =>
  http.delete<void>(`${BASE}/roadmaps/${id}`)

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export const getSteps = (roadmapId: string) =>
  http.get<StepDto[]>(`${BASE}/roadmaps/${roadmapId}/steps`)

export const createStep = (roadmapId: string, data: StepUpsertDto) =>
  http.post<StepDto>(`${BASE}/roadmaps/${roadmapId}/steps`, data)

export const updateStep = (stepId: string, data: StepUpsertDto) =>
  http.put<StepDto>(`${BASE}/steps/${stepId}`, data)

export const deleteStep = (stepId: string) =>
  http.delete<void>(`${BASE}/steps/${stepId}`)

export const addStepDependency = (stepId: string, data: StepDependencyDto) =>
  http.post<StepDependencyResponseDto>(
    `${BASE}/steps/${stepId}/dependencies`,
    data,
  )

export const removeStepDependency = (stepId: string, dependsOnStepId: string) =>
  http.delete<void>(
    `${BASE}/steps/${stepId}/dependencies/${dependsOnStepId}`,
  )

// ---------------------------------------------------------------------------
// Ranks (nested under a roadmap for list/create)
// ---------------------------------------------------------------------------

export const getRanks = (roadmapId: string) =>
  http.get<RoadmapRank[]>(`${BASE}/roadmaps/${roadmapId}/ranks`)

export const getRank = (rankId: string) =>
  http.get<RoadmapRank>(`${BASE}/ranks/${rankId}`)

export const createRank = (roadmapId: string, data: RankUpsert) =>
  http.post<RoadmapRank>(`${BASE}/roadmaps/${roadmapId}/ranks`, data)

export const updateRank = (rankId: string, data: RankUpsert) =>
  http.put<RoadmapRank>(`${BASE}/ranks/${rankId}`, data)

export const deleteRank = (rankId: string) =>
  http.delete<void>(`${BASE}/ranks/${rankId}`)

// ---------------------------------------------------------------------------
// Quest Groups
// ---------------------------------------------------------------------------

export const getQuestGroups = (roadmapId: string) =>
  http.get<QuestGroupDto[]>(`${BASE}/roadmaps/${roadmapId}/quest-groups`)

export const createQuestGroup = (
  roadmapId: string,
  data: QuestGroupUpsertDto,
) => http.post<QuestGroupDto>(`${BASE}/roadmaps/${roadmapId}/quest-groups`, data)

export const updateQuestGroup = (groupId: string, data: QuestGroupUpsertDto) =>
  http.put<QuestGroupDto>(`${BASE}/quest-groups/${groupId}`, data)

export const deleteQuestGroup = (groupId: string) =>
  http.delete<void>(`${BASE}/quest-groups/${groupId}`)

// ---------------------------------------------------------------------------
// Quests
// ---------------------------------------------------------------------------

/**
 * Every active continuous quest (CONTINUOUS_TOPUP / CONTINUOUS_SPENDING) in the edition, with its
 * time window and owning roadmap.
 *
 * The one-open-at-a-time invariant is edition-wide, but the roadmap builder only holds one roadmap's
 * steps — so a clashing quest in a sibling or nested roadmap is invisible to it without this call.
 */
export const getContinuousQuests = (festivalEditionId: string) =>
  http.get<ContinuousQuestSummary[]>(`${BASE}/quests/continuous`, {
    params: { festivalEditionId },
  })

export const createQuest = (groupId: string, data: QuestUpsertDto) =>
  http.post<QuestDto>(`${BASE}/quest-groups/${groupId}/quests`, data)

/**
 * Creates a standalone quest (no group) under a roadmap — targeted directly by a step
 * (targetType = QUEST). The quest is linked to the roadmap only via the owning step.
 */
export const createStandaloneQuest = (roadmapId: string, data: QuestUpsertDto) =>
  http.post<QuestDto>(`${BASE}/roadmaps/${roadmapId}/quests`, data)

export const updateQuest = (questId: string, data: QuestUpsertDto) =>
  http.put<QuestDto>(`${BASE}/quests/${questId}`, data)

export const deleteQuest = (questId: string) =>
  http.delete<void>(`${BASE}/quests/${questId}`)

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export const getBadges = (partnerId?: string) =>
  http.get<BadgeDto[]>(`${BASE}/badges`, {
    params: partnerId ? { partnerId } : undefined,
  })

export const getBadge = (id: string) =>
  http.get<BadgeDto>(`${BASE}/badges/${id}`)

export const createBadge = (data: BadgeUpsertDto) =>
  http.post<BadgeDto>(`${BASE}/badges`, data)

export const updateBadge = (id: string, data: BadgeUpsertDto) =>
  http.put<BadgeDto>(`${BASE}/badges/${id}`, data)

export const deleteBadge = (id: string) =>
  http.delete<void>(`${BASE}/badges/${id}`)

// ---------------------------------------------------------------------------
// Reward Definitions (edition-scoped: festivalEditionId query on list + create)
// ---------------------------------------------------------------------------

export const getRewardDefinitions = (festivalEditionId: string) =>
  http.get<RewardDefinitionDto[]>(`${BASE}/reward-definitions`, {
    params: { festivalEditionId },
  })

export const getRewardDefinition = (id: string) =>
  http.get<RewardDefinitionDto>(`${BASE}/reward-definitions/${id}`)

export const createRewardDefinition = (
  festivalEditionId: string,
  data: RewardDefinitionUpsertDto,
) =>
  http.post<RewardDefinitionDto>(`${BASE}/reward-definitions`, data, {
    params: { festivalEditionId },
  })

export const updateRewardDefinition = (
  id: string,
  data: RewardDefinitionUpsertDto,
) => http.put<RewardDefinitionDto>(`${BASE}/reward-definitions/${id}`, data)

export const deleteRewardDefinition = (id: string) =>
  http.delete<void>(`${BASE}/reward-definitions/${id}`)

// ---------------------------------------------------------------------------
// Reward Rules (nested under a reward definition for list/create)
// ---------------------------------------------------------------------------

export const getRewardRules = (defId: string) =>
  http.get<RewardRuleDto[]>(`${BASE}/reward-definitions/${defId}/rules`)

export const createRewardRule = (defId: string, data: RewardRuleUpsertDto) =>
  http.post<RewardRuleDto>(`${BASE}/reward-definitions/${defId}/rules`, data)

export const updateRewardRule = (id: string, data: RewardRuleUpsertDto) =>
  http.put<RewardRuleDto>(`${BASE}/reward-rules/${id}`, data)

export const deleteRewardRule = (id: string) =>
  http.delete<void>(`${BASE}/reward-rules/${id}`)

// ---------------------------------------------------------------------------
// Reward Pools (filter by partnerId/roadmapId; no edition param)
// ---------------------------------------------------------------------------

export const getRewardPools = (params?: {
  partnerId?: string
  roadmapId?: string
}) =>
  http.get<RewardPoolDto[]>(`${BASE}/reward-pools`, {
    params: params && (params.partnerId || params.roadmapId) ? params : undefined,
  })

export const getRewardPool = (id: string) =>
  http.get<RewardPoolDto>(`${BASE}/reward-pools/${id}`)

export const createRewardPool = (data: RewardPoolUpsertDto) =>
  http.post<RewardPoolDto>(`${BASE}/reward-pools`, data)

export const updateRewardPool = (id: string, data: RewardPoolUpsertDto) =>
  http.put<RewardPoolDto>(`${BASE}/reward-pools/${id}`, data)

export const deleteRewardPool = (id: string) =>
  http.delete<void>(`${BASE}/reward-pools/${id}`)

// ---------------------------------------------------------------------------
// Leaderboards (edition-scoped: festivalEditionId query on list + create;
// DEFAULT boards are auto-provisioned server-side, CUSTOM boards are authored here)
// ---------------------------------------------------------------------------

export const getLeaderboards = (festivalEditionId: string) =>
  http.get<LeaderboardDto[]>(`${BASE}/leaderboards`, {
    params: { festivalEditionId },
  })

export const getLeaderboard = (id: string) =>
  http.get<LeaderboardDto>(`${BASE}/leaderboards/${id}`)

export const getLeaderboardStandings = (
  id: string,
  limit?: number,
  locale?: string,
) =>
  http.get<LeaderboardStandingsDto>(`${BASE}/leaderboards/${id}/standings`, {
    params: limit ? { limit } : undefined,
    headers: locale ? { 'x-locale': locale } : undefined,
  })

export const createLeaderboard = (
  festivalEditionId: string,
  data: LeaderboardUpsertDto,
) =>
  http.post<LeaderboardDto>(`${BASE}/leaderboards`, data, {
    params: { festivalEditionId },
  })

export const updateLeaderboard = (id: string, data: LeaderboardUpsertDto) =>
  http.put<LeaderboardDto>(`${BASE}/leaderboards/${id}`, data)

export const setLeaderboardActive = (id: string, active: boolean) =>
  http.put<LeaderboardDto>(`${BASE}/leaderboards/${id}/active`, { active })

/** Works for DEFAULT boards too — ordinal and active are the only knobs they expose. */
export const setLeaderboardOrdinal = (id: string, ordinal: number) =>
  http.put<LeaderboardDto>(`${BASE}/leaderboards/${id}/ordinal`, { ordinal })

export const regenerateLeaderboard = (id: string) =>
  http.post<number>(`${BASE}/leaderboards/${id}/regenerate`)

export const deleteLeaderboard = (id: string) =>
  http.delete<void>(`${BASE}/leaderboards/${id}`)

// ---------------------------------------------------------------------------
// Available Token Pools (edition-scoped: festivalEditionId query on list + create)
// ---------------------------------------------------------------------------

export const getTokenPools = (festivalEditionId: string) =>
  http.get<AvailableTokenPool[]>(`${BASE}/token-pools`, {
    params: { festivalEditionId },
  })

export const createTokenPool = (
  festivalEditionId: string,
  data: { name: string; active?: boolean },
) =>
  http.post<AvailableTokenPool>(`${BASE}/token-pools`, data, {
    params: { festivalEditionId },
  })

// ---------------------------------------------------------------------------
// Tags (edition-scoped: festivalEditionId query on list + create; attachable to
// quests and quest groups, each with its own ordinal — see the association
// endpoints below)
// ---------------------------------------------------------------------------

export const getTags = (festivalEditionId: string) =>
  http.get<TagDto[]>(`${BASE}/tags`, { params: { festivalEditionId } })

export const createTag = (festivalEditionId: string, data: TagUpsertDto) =>
  http.post<TagDto>(`${BASE}/tags`, data, {
    params: { festivalEditionId },
  })

export const updateTag = (tagId: string, data: TagUpsertDto) =>
  http.put<TagDto>(`${BASE}/tags/${tagId}`, data)

/** Detaches the tag from every quest/quest group it's attached to. */
export const deleteTag = (tagId: string) =>
  http.delete<void>(`${BASE}/tags/${tagId}`)

// ---------------------------------------------------------------------------
// Quest <-> Tag associations (each call returns the quest's full ordered tags)
// ---------------------------------------------------------------------------

export const getQuestTags = (questId: string) =>
  http.get<TagDto[]>(`${BASE}/quests/${questId}/tags`)

export const attachQuestTag = (questId: string, tagId: string) =>
  http.post<TagDto[]>(`${BASE}/quests/${questId}/tags/${tagId}`)

export const detachQuestTag = (questId: string, tagId: string) =>
  http.delete<TagDto[]>(`${BASE}/quests/${questId}/tags/${tagId}`)

export const reorderQuestTags = (questId: string, tagIds: string[]) =>
  http.patch<TagDto[]>(`${BASE}/quests/${questId}/tags/reorder`, { tagIds })

// ---------------------------------------------------------------------------
// Quest Group <-> Tag associations (each call returns the group's full ordered tags)
// ---------------------------------------------------------------------------

export const getQuestGroupTags = (groupId: string) =>
  http.get<TagDto[]>(`${BASE}/quest-groups/${groupId}/tags`)

export const attachQuestGroupTag = (groupId: string, tagId: string) =>
  http.post<TagDto[]>(`${BASE}/quest-groups/${groupId}/tags/${tagId}`)

export const detachQuestGroupTag = (groupId: string, tagId: string) =>
  http.delete<TagDto[]>(`${BASE}/quest-groups/${groupId}/tags/${tagId}`)

export const reorderQuestGroupTags = (groupId: string, tagIds: string[]) =>
  http.patch<TagDto[]>(`${BASE}/quest-groups/${groupId}/tags/reorder`, {
    tagIds,
  })
